import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { TonConnectButton, useTonWallet } from "@tonconnect/ui-react";
import { useWalletDebug } from "../hooks/useWalletDebug";
import { useTelegram } from "../providers/TelegramProvider";
import { toast } from "../components/ui/Toast";
import "../styles/main.scss";

interface ReferralLandingProps {
    onReferralRegistered?: () => void;
}

export default function ReferralLanding({ onReferralRegistered }: ReferralLandingProps): JSX.Element {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const wallet = useTonWallet();
    const { user: tgUser, startParam: tgStartParam } = useTelegram();
    const [status, setStatus] = useState<"checking" | "registering" | "ready">("checking");
    const [referralCode, setReferralCode] = useState<string | null>(null);

    // Debug hook
    useWalletDebug();

    // Check for referral code from multiple sources (priority order)
    useEffect(() => {
        console.log('🔍 Checking for referral code...', {
            tgStartParam,
            hash: window.location.hash,
            search: searchParams.toString()
        });

        // Priority 1: Telegram start_param (BEST - from deep link)
        if (tgStartParam) {
            // start_param will be "ref_CODE123"
            const code = tgStartParam.replace('ref_', '').toUpperCase();
            console.log('✅ Found referral code in Telegram start_param:', code);
            setReferralCode(code);
            localStorage.setItem("jetp2p_referral_code", code);
            setStatus("ready");
            return;
        }
        
        // Priority 2: URL hash (#ref=CODE)
        const hashRef = window.location.hash.match(/#ref=([A-Za-z0-9]+)/i);
        if (hashRef?.[1]) {
            const code = hashRef[1].toUpperCase();
            console.log('✅ Found referral code in URL hash:', code);
            setReferralCode(code);
            localStorage.setItem("jetp2p_referral_code", code);
            setStatus("ready");
            return;
        }
        
        // Priority 3: URL query (?ref=CODE) - fallback
        const queryRef = searchParams.get("ref");
        if (queryRef) {
            const code = queryRef.toUpperCase();
            console.log('✅ Found referral code in URL query:', code);
            setReferralCode(code);
            localStorage.setItem("jetp2p_referral_code", code);
            setStatus("ready");
            return;
        }

        // Priority 4: localStorage (if user already visited)
        const storedCode = localStorage.getItem("jetp2p_referral_code");
        if (storedCode) {
            console.log('✅ Found referral code in localStorage:', storedCode);
            setReferralCode(storedCode);
        }
        
        setStatus("ready");
    }, [tgStartParam]);

    // Register referral when wallet is connected
    useEffect(() => {
        const registerReferral = async () => {
            if (wallet?.account?.address && referralCode && status === "ready") {
                await registerReferralCode();
            }
        };

        registerReferral();
    }, [wallet, referralCode, status]);

    const registerReferralCode = async () => {
        if (!wallet?.account?.address || !referralCode) return;

        setStatus("registering");
        try {
            const wsClient = await import("../ws/client");
            const response: import("../ws/types").JsonRpcResponse<{ referral: any }> = 
                await wsClient.rpcCall("referral.register", {
                    referrer_code: referralCode,
                    referred_wallet: wallet.account.address
                });

            if (response.error) {
                if (response.error.message?.includes("already referred")) {
                    localStorage.setItem("jetp2p_referral_registered", "true");
                    toast.info("Welcome back! Your referral is already registered.");
                    onReferralRegistered?.();
                }
            } else if (response.result?.referral) {
                localStorage.setItem("jetp2p_referral_registered", "true");
                toast.success("Referral registered successfully!");
                onReferralRegistered?.();
            }
        } catch (error) {
            console.error("Failed to register referral:", error);
        }
        setStatus("ready");
    };

    const handleStartTrading = () => {
        navigate("/");
    };

    const handleGetReferralLink = () => {
        navigate("/?tab=referral");
    };

    if (status === "checking") {
        return (
            <div className="landing-page">
                <div className="loading-container">
                    <div className="spinner-lg" />
                    <span className="loading-text">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="landing-page">
            <div className="landing-container">
                {/* Header */}
                <header className="landing-header">
                    <h1>🚀 Jet P2P</h1>
                    <p className="tagline">Secure P2P Trading on TON Network</p>
                    <TonConnectButton />
                </header>

                {/* Hero Section */}
                <section className="hero">
                    <div className="hero-content">
                        <h2>Trade Crypto Securely with Escrow</h2>
                        <ul className="features">
                            <li>✅ Instant escrow protection</li>
                            <li>✅ Low fees (0.5%)</li>
                            <li>✅ Telegram mini-app</li>
                            <li>✅ 100% secure & transparent</li>
                        </ul>
                    </div>
                </section>

                {/* Referral Banner */}
                {referralCode && (
                    <section className="referral-banner">
                        <div className="banner-content">
                            <span className="banner-icon">🎁</span>
                            <div>
                                <h3>Welcome! You were invited by a friend</h3>
                                <p>Your friend will earn 10% commission on your trades!</p>
                            </div>
                        </div>
                    </section>
                )}

                {/* Stats Section */}
                <section className="stats-section">
                    <div className="stat-card">
                        <span className="stat-number">50K+</span>
                        <span className="stat-label">Trades Completed</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-number">0.5%</span>
                        <span className="stat-label">Trading Fee</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-number">10%</span>
                        <span className="stat-label">Referral Reward</span>
                    </div>
                </section>

                {/* How It Works */}
                <section className="how-it-works">
                    <h2>How It Works</h2>
                    <div className="steps">
                        <div className="step">
                            <span className="step-num">1</span>
                            <h4>Connect Wallet</h4>
                            <p>Link your TON wallet securely</p>
                        </div>
                        <div className="step">
                            <span className="step-num">2</span>
                            <h4>Create/Find Order</h4>
                            <p>Browse or create P2P listings</p>
                        </div>
                        <div className="step">
                            <span className="step-num">3</span>
                            <h4>Trade Safely</h4>
                            <p>Escrow protects both parties</p>
                        </div>
                        <div className="step">
                            <span className="step-num">4</span>
                            <h4>Complete & Earn</h4>
                            <p>Get your crypto instantly</p>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="cta-section">
                    {wallet ? (
                        <button onClick={handleStartTrading} className="cta-btn primary">
                            🎯 Start Trading Now
                        </button>
                    ) : (
                        <div className="wallet-prompt">
                            <p>Connect your wallet to start trading</p>
                            <TonConnectButton />
                        </div>
                    )}
                </section>

                {/* Referral CTA (if logged in) */}
                {wallet && (
                    <section className="referral-cta">
                        <p>Know friends who want to trade?</p>
                        <button onClick={handleGetReferralLink} className="cta-btn secondary">
                            🔗 Get Your Referral Link
                        </button>
                    </section>
                )}

                {/* Footer */}
                <footer className="landing-footer">
                    <p>Powered by TON Network</p>
                    <p className="disclaimer">Trade responsibly. Always verify counterparties.</p>
                </footer>
            </div>
        </div>
    );
}
